/* A short opening in the frame, then still photographs. Commerce stays still. */
(() => {
  'use strict';
  if (window.YulFishScrollMotion) return;

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const entered = new WeakSet();
  const waiting = new Map();
  const running = new Map();
  const homes = new Set();
  const recipes = [
    {kind: 'coast', selector: '.yf-home-chapter--coast > img', from: 'inset(0 0 12% 0)', duration: 700},
    {kind: 'category', selector: '.yf-home-category-image > img', from: 'inset(0 7% 0 0)', duration: 460},
    {kind: 'guide', selector: '.yf-home-guide-image img', from: 'inset(0 0 0 9%)', duration: 560}
  ];
  let initialized = false, lifetime = null, observer = null, frame = 0, generation = 0;
  const moving = () => initialized && !document.hidden && !reduced?.matches && !window.YulFishMotionPreference?.paused;
  const visible = image => {
    if (!image?.isConnected || image.closest('[hidden]')) return false;
    const rect = image.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth && rect.width > 0 && rect.height > 0;
  };

  function settle(record) {
    if (!record) return;
    record.cancelled = true;
    record.cleanup?.();
    if (waiting.get(record.image) === record) waiting.delete(record.image);
    if (record.animation) {
      record.animation.onfinish = null;
      record.animation.oncancel = null;
      record.animation.cancel();
    }
    if (running.get(record.image) === record) running.delete(record.image);
    record.image.classList.remove('yf-scroll-opening');
    record.image.dataset.yfScrollState = record.kind === 'river' ? 'idle' : 'done';
  }

  function cleanup() {
    generation++;
    observer?.disconnect();
    observer = null;
    waiting.forEach(settle);
    running.forEach(settle);
    homes.forEach(home => { home.dataset.yfMotionPaused = 'true'; });
  }

  function open(record) {
    record.cleanup?.();
    record.cleanup = null;
    if (waiting.get(record.image) === record) waiting.delete(record.image);
    if (record.cancelled || record.generation !== generation) return;
    if (record.kind === 'river') {
      if (moving() && record.image.isConnected && record.image.naturalWidth) record.image.dataset.yfScrollState = 'ready';
      else settle(record);
      return;
    }
    if (!moving() || !visible(record.image) || !record.image.naturalWidth || typeof record.image.animate !== 'function') {
      settle(record);
      return;
    }
    const {image, recipe, delay} = record;
    image.classList.add('yf-scroll-opening');
    image.dataset.yfScrollState = 'running';
    try {
      record.animation = image.animate(
        [{clipPath: recipe.from}, {clipPath: 'inset(0 0 0% 0)'}],
        {duration: recipe.duration, delay, easing: 'cubic-bezier(.22,1,.36,1)', iterations: 1, fill: 'backwards'}
      );
      running.set(image, record);
      record.animation.onfinish = () => settle(record);
      record.animation.oncancel = () => settle(record);
    } catch {
      settle(record);
    }
  }

  function prepare(image, recipe, delay = 0) {
    if (waiting.has(image) || running.has(image)) return;
    const record = {image, recipe, kind: recipe.kind, delay, generation, cancelled: false, cleanup: null};
    image.dataset.yfScrollMotion = recipe.kind;
    image.dataset.yfScrollState = 'waiting';
    waiting.set(image, record);
    const decode = () => {
      record.cleanup?.();
      record.cleanup = null;
      if (record.cancelled || record.generation !== generation) return;
      if (!image.naturalWidth) { settle(record); return; }
      if (typeof image.decode !== 'function') { open(record); return; }
      try {
        Promise.resolve(image.decode()).catch(() => {}).then(() => open(record));
      } catch {
        open(record);
      }
    };
    if (image.complete) decode();
    else {
      const failed = () => settle(record);
      image.addEventListener('load', decode, {once: true});
      image.addEventListener('error', failed, {once: true});
      record.cleanup = () => {
        image.removeEventListener('load', decode);
        image.removeEventListener('error', failed);
      };
    }
  }

  function bind() {
    if (!initialized) return;
    homes.clear();
    document.querySelectorAll('#app .yf-home,#app.yf-home,.yulfish-native .yf-home').forEach(home => {
      homes.add(home);
      home.dataset.yfScrollReady = 'true';
      home.dataset.yfMotionPaused = moving() ? 'false' : 'true';
    });
    if (!moving()) return;
    const targets = new Map();
    homes.forEach(home => {
      recipes.forEach(recipe => home.querySelectorAll(recipe.selector).forEach(image => {
        image.dataset.yfScrollMotion = recipe.kind;
        if (entered.has(image)) { image.dataset.yfScrollState = 'done'; return; }
        if (!image.isConnected || image.closest('[hidden]')) return;
        targets.set(image, recipe);
      }));
      home.querySelectorAll('.yf-home-chapter--river > img').forEach(image => {
        image.dataset.yfScrollMotion = 'river';
        if (image.dataset.yfScrollState !== 'ready') prepare(image, {kind: 'river'});
      });
    });
    if (typeof window.IntersectionObserver !== 'function' || !targets.size) return;
    const pageGeneration = generation;
    const active = new window.IntersectionObserver(entries => {
      if (observer !== active || pageGeneration !== generation || !moving()) return;
      let categoryIndex = 0;
      entries.forEach(entry => {
        if (!entry.isIntersecting || !targets.has(entry.target)) return;
        const image = entry.target, recipe = targets.get(image);
        active.unobserve(image);
        targets.delete(image);
        if (entered.has(image)) return;
        entered.add(image);
        prepare(image, recipe, recipe.kind === 'category' ? Math.min(categoryIndex++ * 32, 128) : 0);
      });
      if (!targets.size) {
        active.disconnect();
        if (observer === active) observer = null;
      }
    }, {threshold: .08, rootMargin: '0px 0px -12px 0px'});
    observer = active;
    targets.forEach((recipe, image) => active.observe(image));
  }

  function refresh() {
    if (!initialized) return;
    window.cancelAnimationFrame(frame);
    frame = 0;
    cleanup();
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      bind();
    });
  }

  function suspend() {
    window.cancelAnimationFrame(frame);
    frame = 0;
    cleanup();
  }

  function preferenceChanged() {
    if (moving()) refresh();
    else suspend();
  }

  function init() {
    if (initialized) return;
    initialized = true;
    lifetime = new AbortController();
    const options = {signal: lifetime.signal};
    document.addEventListener('yulfish:route', refresh, options);
    document.addEventListener('yulfish:motion-preference', preferenceChanged, options);
    document.addEventListener('visibilitychange', preferenceChanged, options);
    window.addEventListener('pagehide', suspend, options);
    window.addEventListener('pageshow', refresh, options);
    if (reduced?.addEventListener) reduced.addEventListener('change', preferenceChanged, options);
    else if (reduced?.addListener) reduced.addListener(preferenceChanged);
    refresh();
  }

  function destroy() {
    initialized = false;
    document.removeEventListener('DOMContentLoaded', init);
    lifetime?.abort();
    lifetime = null;
    if (!reduced?.removeEventListener) reduced?.removeListener?.(preferenceChanged);
    suspend();
    homes.forEach(home => {
      delete home.dataset.yfScrollReady;
      home.querySelectorAll('[data-yf-scroll-motion]').forEach(image => {
        delete image.dataset.yfScrollMotion;
        delete image.dataset.yfScrollState;
      });
    });
    homes.clear();
  }

  window.YulFishScrollMotion = Object.freeze({init, refresh, destroy});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
